import React, { useState, useMemo, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBoxes,
  faPlus,
  faEdit,
  faTrash,
  faSearch,
  faFilter,
  faSpinner,
  faTimes,
  faRefresh,
  faClose,
  faCheckCircle,
  faExclamationTriangle,
  faClock,
  faMapMarkerAlt,
  faCalendarAlt,
  faBoxOpen,
  faLowVision,
  faArrowUp,
  faArrowDown,
  faSort,
  faWarehouse,
  faDollarSign,
  faTruck,
  faExchangeAlt,
} from "@fortawesome/free-solid-svg-icons";
import useSWR from "swr";
import DashboardContenLeftMenus from "./DashboardContenLeftMenus";
import DashboardContentHeader from "./DashboardContentHeader";
import DashboardContentFooter from "./DashboardContentFooter";
import TopActionMenus from "./TopActionMenus";
import MessageModal from "./MessageModal";
import { useNavigationEvent } from "navigation-react";

// API fetcher for custom API
const customFetcher = async (url) => {
  const response = await fetch(url, {
    credentials: "include",
    headers: {
      Accept: "application/json",
    },
  });
  if (!response.ok)
    throw new Error(`API request failed with status ${response.status}`);
  return response.json();
};

// Stock level status constants
const STOCK_STATUS = {
  CRITICAL: {
    label: "Critical",
    color: "#EF4444",
    bgColor: "#FEE2E2",
  },
  LOW: {
    label: "Low Stock",
    color: "#F59E0B",
    bgColor: "#FEF3C7",
  },
  NORMAL: {
    label: "In Stock",
    color: "#10B981",
    bgColor: "#D1FAE5",
  },
  EXCESS: {
    label: "Excess",
    color: "#3B82F6",
    bgColor: "#EFF6FF",
  },
  EXPIRED: {
    label: "Expired",
    color: "#6B7280",
    bgColor: "#F3F4F6",
  },
  OUT_OF_STOCK: {
    label: "Out of Stock",
    color: "#DC2626",
    bgColor: "#FEF2F2",
  },
};

// Storage location options
const STORAGE_LOCATIONS = [
  "Walk-in Fridge",
  "Freezer",
  "Storage Room A",
  "Storage Room B",
  "Dry Storage",
  "Kitchen",
  "Bar",
  "Warehouse",
];

export default function Inventory() {
  const { stateNavigator } = useNavigationEvent();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [locationFilter, setLocationFilter] = useState("all");
  const [sortField, setSortField] = useState("item_name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showReceiveStockModal, setShowReceiveStockModal] = useState(false);
  const [showTransferStockModal, setShowTransferStockModal] = useState(false);
  const [showAddSupplierModal, setShowAddSupplierModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalSuccessMsg, setModalSuccessMsg] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [modalError, setModalError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [showBatchesModal, setShowBatchesModal] = useState(false);
  const [batchesData, setBatchesData] = useState(null);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [newSupplier, setNewSupplier] = useState({
    name: "",
    contact_person: "",
    phone: "",
    email: "",
    address: "",
  });
  const [addingSupplier, setAddingSupplier] = useState(false);

  // Receive stock form
  const [receiveForm, setReceiveForm] = useState({
    quantity: 0,
    purchase_order: "",
    unit_cost: "",
    notes: "",
  });

  // Transfer stock form
  const [transferForm, setTransferForm] = useState({
    quantity: 0,
    destination: "",
    reason: "",
  });

  const [formData, setFormData] = useState({
    item_name: "",
    quantity: 0,
    storage_location: "",
    expiry_date: "",
    reorder_level: 10,
    unit_price: 0,
    supplier_id: "",
    batch_number: "",
    notes: "",
  });
  const [editFormData, setEditFormData] = useState({
    item_name: "",
    quantity: 0,
    storage_location: "",
    expiry_date: "",
    reorder_level: 10,
    unit_price: 0,
    supplier_id: "",
    batch_number: "",
    notes: "",
  });

  // Fetch suppliers from Drupal
  const { data: suppliersData, mutate: mutateSuppliers } = useSWR(
    `${process.env.REACT_APP_BASE_URL}/jsonapi/node/supplier?sort=title`,
    customFetcher,
    { revalidateOnFocus: true },
  );

  // Fetch inventory items from custom API
  const {
    data: inventoryData,
    error: inventoryError,
    isLoading: inventoryLoading,
    mutate: mutateInventory,
  } = useSWR(
    `${process.env.REACT_APP_BASE_URL}/api/inventory/items`,
    customFetcher,
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      refreshInterval: 30000,
    },
  );

  // Fetch inventory statistics
  const { data: statisticsData } = useSWR(
    `${process.env.REACT_APP_BASE_URL}/api/inventory/statistics`,
    customFetcher,
    {
      revalidateOnFocus: true,
      refreshInterval: 60000,
    },
  );

  // Fetch departments for transfer
  const { data: departmentsData } = useSWR(
    `${process.env.REACT_APP_BASE_URL}/api/departments`,
    customFetcher,
    {
      revalidateOnFocus: true,
    },
  );

  // Process suppliers list - store both UUID and numeric ID
  const suppliers = useMemo(() => {
    if (!suppliersData?.data) return [];
    return suppliersData.data.map((supplier) => ({
      id: supplier.id,
      nid: supplier.attributes.drupal_internal__nid,
      name: supplier.attributes.title,
      contact_person: supplier.attributes.field_contact_person || "",
      phone: supplier.attributes.field_phone || "",
      email: supplier.attributes.field_supplier_email || "",
    }));
  }, [suppliersData]);

  // Process inventory items
  const inventoryItems = useMemo(() => {
    if (!inventoryData || !inventoryData.data) return [];

    const supplierLookup = {};
    suppliers.forEach((supplier) => {
      supplierLookup[supplier.nid] = supplier.name;
    });

    return inventoryData.data.map((item) => {
      const currentDate = new Date();
      const expiryDate = item.expiry_date ? new Date(item.expiry_date) : null;
      const quantity = item.quantity || 0;
      const reorderLevel = item.reorder_level || 10;

      let stockStatus = STOCK_STATUS.NORMAL;
      if (expiryDate && expiryDate < currentDate) {
        stockStatus = STOCK_STATUS.EXPIRED;
      } else if (quantity <= 0) {
        stockStatus = STOCK_STATUS.OUT_OF_STOCK;
      } else if (quantity <= reorderLevel / 2) {
        stockStatus = STOCK_STATUS.CRITICAL;
      } else if (quantity <= reorderLevel) {
        stockStatus = STOCK_STATUS.LOW;
      } else if (quantity > reorderLevel * 3) {
        stockStatus = STOCK_STATUS.EXCESS;
      }

      return {
        id: item.id,
        item_name: item.item_name,
        quantity: quantity,
        storage_location: item.storage_location || "",
        expiry_date: item.expiry_date,
        reorder_level: reorderLevel,
        unit_price: item.unit_price || 0,
        supplier_id: item.supplier_id || "",
        supplier_name: item.supplier_id ? supplierLookup[item.supplier_id] : "",
        batch_number: item.batch_number || "",
        stock_status: stockStatus,
        days_until_expiry: expiryDate
          ? Math.ceil((expiryDate - currentDate) / (1000 * 60 * 60 * 24))
          : null,
      };
    });
  }, [inventoryData, suppliers]);

  // Get unique storage locations for filter
  const storageLocations = useMemo(() => {
    const locations = new Set();
    inventoryItems.forEach((item) => {
      if (item.storage_location) locations.add(item.storage_location);
    });
    if (locations.size === 0) {
      STORAGE_LOCATIONS.forEach((loc) => locations.add(loc));
    }
    return Array.from(locations);
  }, [inventoryItems]);

  // Get departments list
  const departments = useMemo(() => {
    if (!departmentsData || !departmentsData.data) return [];
    return departmentsData.data;
  }, [departmentsData]);

  // Filter and sort inventory items
  const filteredItems = useMemo(() => {
    let filtered = [...inventoryItems];

    if (statusFilter !== "all") {
      filtered = filtered.filter((item) => {
        if (statusFilter === "critical")
          return item.stock_status === STOCK_STATUS.CRITICAL;
        if (statusFilter === "low")
          return item.stock_status === STOCK_STATUS.LOW;
        if (statusFilter === "normal")
          return item.stock_status === STOCK_STATUS.NORMAL;
        if (statusFilter === "excess")
          return item.stock_status === STOCK_STATUS.EXCESS;
        if (statusFilter === "expired")
          return item.stock_status === STOCK_STATUS.EXPIRED;
        if (statusFilter === "out_of_stock")
          return item.stock_status === STOCK_STATUS.OUT_OF_STOCK;
        return true;
      });
    }

    if (locationFilter !== "all") {
      filtered = filtered.filter(
        (item) => item.storage_location === locationFilter,
      );
    }

    if (searchTerm) {
      filtered = filtered.filter(
        (item) =>
          item.item_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (item.supplier_name &&
            item.supplier_name
              .toLowerCase()
              .includes(searchTerm.toLowerCase())) ||
          (item.batch_number &&
            item.batch_number.toLowerCase().includes(searchTerm.toLowerCase())),
      );
    }

    filtered.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      if (sortField === "item_name") {
        aVal = a.item_name;
        bVal = b.item_name;
      } else if (sortField === "quantity") {
        aVal = a.quantity;
        bVal = b.quantity;
      } else if (sortField === "unit_price") {
        aVal = a.unit_price;
        bVal = b.unit_price;
      } else if (sortField === "days_until_expiry") {
        aVal = a.days_until_expiry || Infinity;
        bVal = b.days_until_expiry || Infinity;
      }

      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [
    inventoryItems,
    statusFilter,
    locationFilter,
    searchTerm,
    sortField,
    sortDirection,
  ]);

  // Pagination
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
  const paginatedItems = filteredItems.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  // Calculate statistics
  const statistics = useMemo(() => {
    if (!statisticsData || !statisticsData.statistics) {
      return {
        totalItems: 0,
        totalQuantity: 0,
        lowStockItems: 0,
        expiredItems: 0,
        outOfStockItems: 0,
        normalStockItems: 0,
        totalValue: 0,
        expiringSoon: 0,
        uniqueLocations: 0,
      };
    }

    const stats = statisticsData.statistics;
    return {
      totalItems: stats.total_items || 0,
      totalQuantity: stats.total_quantity || 0,
      lowStockItems: stats.low_stock_items || 0,
      expiredItems: stats.expired_items || 0,
      outOfStockItems: stats.out_of_stock_items || 0,
      normalStockItems:
        (stats.total_items || 0) -
        ((stats.low_stock_items || 0) +
          (stats.critical_stock_items || 0) +
          (stats.out_of_stock_items || 0) +
          (stats.expired_items || 0)),
      totalValue: stats.total_value || 0,
      expiringSoon: stats.expiring_soon_items || 0,
      uniqueLocations: stats.unique_locations || 0,
    };
  }, [statisticsData]);

  // Fetch CSRF token
  const fetchCsrfToken = async () => {
    const response = await fetch(
      `${process.env.REACT_APP_BASE_URL}/session/token`,
      {
        method: "GET",
        credentials: "include",
      },
    );
    if (!response.ok) throw new Error("Failed to fetch CSRF token");
    return await response.text();
  };

  // Add new supplier
  const handleAddSupplier = async (e) => {
    e.preventDefault();

    if (!newSupplier.name.trim()) {
      setErrorMessage("Supplier name is required");
      setModalError(true);
      return;
    }

    setAddingSupplier(true);

    try {
      const csrfToken = await fetchCsrfToken();

      const payload = {
        data: {
          type: "node--supplier",
          attributes: {
            title: newSupplier.name,
            field_contact_person: newSupplier.contact_person || "",
            field_phone: newSupplier.phone || "",
            field_supplier_email: newSupplier.email || "",
            field_address: newSupplier.address || "",
          },
        },
      };

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/jsonapi/node/supplier`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/vnd.api+json",
            Accept: "application/vnd.api+json",
            "X-CSRF-Token": csrfToken,
          },
          credentials: "include",
          body: JSON.stringify(payload),
        },
      );

      if (response.ok) {
        const result = await response.json();
        await mutateSuppliers();

        const newSupplierNid = result.data.attributes.drupal_internal__nid;

        setFormData({ ...formData, supplier_id: newSupplierNid });
        setEditFormData({ ...editFormData, supplier_id: newSupplierNid });

        setNewSupplier({
          name: "",
          contact_person: "",
          phone: "",
          email: "",
          address: "",
        });
        setShowAddSupplierModal(false);
        setSuccessMessage("Supplier added successfully!");
        setModalSuccessMsg(true);
      } else {
        const errorData = await response.json();
        setErrorMessage(
          errorData.errors?.[0]?.detail || "Failed to add supplier",
        );
        setModalError(true);
      }
    } catch (err) {
      console.error("Error adding supplier:", err);
      setErrorMessage("An error occurred while adding the supplier");
      setModalError(true);
    }
    setAddingSupplier(false);
  };

  // Add inventory item
  const handleAddItem = async (e) => {
    e.preventDefault();

    if (!formData.item_name.trim()) {
      setErrorMessage("Item name is required");
      setModalError(true);
      return;
    }

    setIsSubmitting(true);

    try {
      const requestBody = {
        item_name: formData.item_name,
        quantity: parseInt(formData.quantity) || 0,
        storage_location: formData.storage_location || null,
        expiry_date: formData.expiry_date || null,
        reorder_level: parseInt(formData.reorder_level) || 10,
        unit_price: parseFloat(formData.unit_price) || 0,
        supplier_id: formData.supplier_id || null,
        batch_number: formData.batch_number || null,
        notes: formData.notes || null,
      };

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/inventory/items`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        },
      );

      const result = await response.json();

      if (result.success) {
        await mutateInventory();
        setFormData({
          item_name: "",
          quantity: 0,
          storage_location: "",
          expiry_date: "",
          reorder_level: 10,
          unit_price: 0,
          supplier_id: "",
          batch_number: "",
          notes: "",
        });
        setShowAddModal(false);
        setSuccessMessage(`${formData.item_name} added to inventory!`);
        setModalSuccessMsg(true);
      } else {
        setErrorMessage(result.error || "Failed to add item");
        setModalError(true);
      }
    } catch (err) {
      console.error("Error adding item:", err);
      setErrorMessage("An error occurred while adding the item");
      setModalError(true);
    }
    setIsSubmitting(false);
  };

  const handleEditItem = async (e) => {
    e.preventDefault();

    setIsSubmitting(true);

    try {
      const requestBody = {
        item_name: editFormData.item_name,
        quantity: parseInt(editFormData.quantity) || 0,
        storage_location: editFormData.storage_location || null,
        expiry_date: editFormData.expiry_date || null,
        reorder_level: parseInt(editFormData.reorder_level) || 10,
        unit_price: parseFloat(editFormData.unit_price) || 0,
        supplier_id: editFormData.supplier_id || null,
        batch_number: editFormData.batch_number || null,
        notes: editFormData.notes || null,
      };

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/inventory/items/${selectedItem.id}`,
        {
          method: "PATCH",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        },
      );

      const result = await response.json();

      if (result.success) {
        await mutateInventory();
        setShowEditModal(false);
        setSelectedItem(null);
        setSuccessMessage(`${editFormData.item_name} updated successfully!`);
        setModalSuccessMsg(true);
      } else {
        setErrorMessage(result.error || "Failed to update item");
        setModalError(true);
      }
    } catch (err) {
      console.error("Error updating item:", err);
      setErrorMessage("An error occurred while updating the item");
      setModalError(true);
    }
    setIsSubmitting(false);
  };

  // Receive stock
  const handleReceiveStock = async (e) => {
    e.preventDefault();

    if (!selectedItem) return;
    if (receiveForm.quantity <= 0) {
      setErrorMessage("Please enter a valid quantity");
      setModalError(true);
      return;
    }

    setIsSubmitting(true);

    try {
      const currentQuantity = selectedItem.quantity;
      const currentUnitPrice = selectedItem.unit_price;
      const newQuantity = parseInt(receiveForm.quantity);
      const newUnitCost = receiveForm.unit_cost
        ? parseFloat(receiveForm.unit_cost)
        : currentUnitPrice;

      let updatedUnitPrice = currentUnitPrice;
      if (currentQuantity > 0 && newUnitCost > 0) {
        const totalValue =
          currentQuantity * currentUnitPrice + newQuantity * newUnitCost;
        const totalQuantity = currentQuantity + newQuantity;
        updatedUnitPrice = totalValue / totalQuantity;
      } else if (newUnitCost > 0) {
        updatedUnitPrice = newUnitCost;
      }

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/inventory/receive-stock`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            item_id: selectedItem.id,
            quantity: newQuantity,
            purchase_order: receiveForm.purchase_order,
            unit_cost: newUnitCost,
            notes: receiveForm.notes,
            update_unit_price: true,
            updated_unit_price: updatedUnitPrice,
          }),
        },
      );

      const result = await response.json();

      if (result.success) {
        await mutateInventory();
        setShowReceiveStockModal(false);
        setSelectedItem(null);
        setReceiveForm({
          quantity: 0,
          purchase_order: "",
          unit_cost: "",
          notes: "",
        });
        setSuccessMessage(
          `Received ${newQuantity} units of ${selectedItem.item_name}! New average cost: GH₵${updatedUnitPrice.toFixed(2)}`,
        );
        setModalSuccessMsg(true);
      } else {
        setErrorMessage(result.error || "Failed to receive stock");
        setModalError(true);
      }
    } catch (err) {
      console.error("Error receiving stock:", err);
      setErrorMessage("An error occurred while receiving stock");
      setModalError(true);
    }
    setIsSubmitting(false);
  };

  // Transfer stock
  const handleTransferStock = async (e) => {
    e.preventDefault();

    if (!selectedItem) return;
    if (transferForm.quantity <= 0) {
      setErrorMessage("Please enter a valid quantity");
      setModalError(true);
      return;
    }
    if (transferForm.quantity > selectedItem.quantity) {
      setErrorMessage("Insufficient stock available");
      setModalError(true);
      return;
    }
    if (!transferForm.destination) {
      setErrorMessage("Please select a destination department");
      setModalError(true);
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/inventory/transfer`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            item_id: selectedItem.id,
            quantity: parseInt(transferForm.quantity),
            destination: transferForm.destination,
            reason: transferForm.reason,
          }),
        },
      );

      const result = await response.json();

      if (result.success) {
        await mutateInventory();
        setShowTransferStockModal(false);
        setSelectedItem(null);
        setTransferForm({ quantity: 0, destination: "", reason: "" });
        setSuccessMessage(
          `Transferred ${transferForm.quantity} units of ${selectedItem.item_name} to ${transferForm.destination}!`,
        );
        setModalSuccessMsg(true);
      } else {
        setErrorMessage(result.error || "Failed to transfer stock");
        setModalError(true);
      }
    } catch (err) {
      console.error("Error transferring stock:", err);
      setErrorMessage("An error occurred while transferring stock");
      setModalError(true);
    }
    setIsSubmitting(false);
  };

  // Delete inventory item
  const handleDeleteItem = async () => {
    if (!selectedItem) return;

    setIsSubmitting(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/inventory/items/${selectedItem.id}`,
        {
          method: "DELETE",
          credentials: "include",
        },
      );

      const result = await response.json();

      if (result.success) {
        await mutateInventory();
        setShowDeleteConfirm(false);
        setSelectedItem(null);
        setSuccessMessage(`${selectedItem.item_name} deleted from inventory!`);
        setModalSuccessMsg(true);
      } else {
        setErrorMessage(result.error || "Failed to delete item");
        setModalError(true);
      }
    } catch (err) {
      console.error("Error deleting item:", err);
      setErrorMessage("An error occurred while deleting the item");
      setModalError(true);
    }
    setIsSubmitting(false);
  };

  // Add function to view batches
  const handleViewBatches = async (item) => {
    setSelectedItem(item);
    setLoadingBatches(true);
    setShowBatchesModal(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/inventory/${item.id}/batches`,
        { credentials: "include" },
      );
      const data = await response.json();
      setBatchesData(data);
    } catch (error) {
      console.error("Error loading batches:", error);
    } finally {
      setLoadingBatches(false);
    }
  };

  const openEditModal = (item) => {
    setSelectedItem(item);
    setEditFormData({
      item_name: item.item_name,
      quantity: item.quantity,
      storage_location: item.storage_location || "",
      expiry_date: item.expiry_date || "",
      reorder_level: item.reorder_level,
      unit_price: item.unit_price,
      supplier_id: item.supplier_id || "",
      batch_number: item.batch_number || "",
      notes: item.notes || "",
    });
    setShowEditModal(true);
  };

  const openReceiveModal = (item) => {
    setSelectedItem(item);
    setReceiveForm({
      quantity: 0,
      purchase_order: "",
      unit_cost: "",
      notes: "",
    });
    setShowReceiveStockModal(true);
  };

  const openTransferModal = (item) => {
    setSelectedItem(item);
    setTransferForm({ quantity: 0, destination: "", reason: "" });
    setShowTransferStockModal(true);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field)
      return <FontAwesomeIcon icon={faSort} style={{ opacity: 0.3 }} />;
    return (
      <FontAwesomeIcon
        icon={sortDirection === "asc" ? faArrowUp : faArrowDown}
      />
    );
  };

  const InventoryTableRow = ({ item }) => {
    const status = item.stock_status;

    return (
      <tr
        style={
          item.days_until_expiry !== null && item.days_until_expiry <= 7
            ? styles.expiringRow
            : {}
        }
      >
        <td style={styles.td}>
          <div>
            <strong>{item.item_name}</strong>
            {item.batch_number && (
              <span style={styles.batchNumber}>Batch: {item.batch_number}</span>
            )}
          </div>
        </td>
        <td style={styles.td}>
          <span
            style={{
              ...styles.quantityBadge,
              backgroundColor:
                status === STOCK_STATUS.CRITICAL
                  ? "#FEE2E2"
                  : status === STOCK_STATUS.LOW
                    ? "#FEF3C7"
                    : "#E8F5E9",
              color:
                status === STOCK_STATUS.CRITICAL
                  ? "#EF4444"
                  : status === STOCK_STATUS.LOW
                    ? "#F59E0B"
                    : "#10B981",
            }}
          >
            {item.quantity} units
          </span>
        </td>
        <td style={styles.td}>
          <span>{item.storage_location || "Not specified"}</span>
        </td>
        <td style={styles.td}>
          <div>
            {item.expiry_date ? (
              <>
                <span
                  style={{
                    color: item.days_until_expiry <= 7 ? "#EF4444" : "#1F2937",
                  }}
                >
                  {new Date(item.expiry_date).toLocaleDateString()}
                </span>
                {item.days_until_expiry <= 30 && item.days_until_expiry > 0 && (
                  <span style={styles.expiryWarning}>
                    {item.days_until_expiry} days left
                  </span>
                )}
              </>
            ) : (
              <span style={{ color: "#9CA3AF" }}>No expiry</span>
            )}
          </div>
        </td>
        <td style={styles.td}>
          <div>
            <span style={styles.reorderText}>
              Reorder at {item.reorder_level}
            </span>
          </div>
        </td>
        <td style={styles.td}>
          <span
            style={{
              ...styles.statusBadge,
              backgroundColor: status.bgColor,
              color: status.color,
            }}
          >
            {status.label}
          </span>
        </td>
        <td style={styles.td}>GH₵{item.unit_price.toFixed(2)}</td>
        <td style={styles.td}>
          <div style={styles.actionButtons}>
            <button
              style={styles.receiveButton}
              onClick={() => openReceiveModal(item)}
              title="Receive Stock"
            >
              <FontAwesomeIcon icon={faTruck} />
            </button>
            <button
              style={styles.transferButton}
              onClick={() => openTransferModal(item)}
              title="Transfer Stock"
            >
              <FontAwesomeIcon icon={faExchangeAlt} />
            </button>
            <button
              style={styles.editButton}
              onClick={() => openEditModal(item)}
              title="Edit"
            >
              <FontAwesomeIcon icon={faEdit} />
            </button>
            <button
              style={styles.deleteButton}
              onClick={() => {
                setSelectedItem(item);
                setShowDeleteConfirm(true);
              }}
              title="Delete"
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>
          </div>
        </td>
      </tr>
    );
  };

  if (inventoryLoading && !inventoryData) {
    return (
      <div className="dashboard-container">
        <DashboardContentHeader />
        <div className="content-wrapper">
          <DashboardContenLeftMenus />
          <div style={styles.container}>
            <div style={styles.loadingContainer}>
              <FontAwesomeIcon
                icon={faSpinner}
                spin
                style={{ fontSize: "48px", color: "#FF6B35" }}
              />
              <p>Loading inventory...</p>
            </div>
          </div>
        </div>
        <DashboardContentFooter />
      </div>
    );
  }

  if (inventoryError) {
    return (
      <div className="dashboard-container">
        <DashboardContentHeader />
        <div className="content-wrapper">
          <DashboardContenLeftMenus />
          <div style={styles.container}>
            <div style={styles.errorContainer}>
              <FontAwesomeIcon
                icon={faTimes}
                style={{ fontSize: "48px", color: "#d32f2f" }}
              />
              <h3>Error Loading Inventory</h3>
              <p>
                Unable to connect to the inventory API. Please check your
                connection.
              </p>
              <button
                style={styles.retryButton}
                onClick={() => mutateInventory()}
              >
                <FontAwesomeIcon
                  icon={faRefresh}
                  style={{ marginRight: "8px" }}
                />
                Retry
              </button>
            </div>
          </div>
        </div>
        <DashboardContentFooter />
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <DashboardContentHeader />
      <div className="content-wrapper">
        <DashboardContenLeftMenus />
        <div style={styles.container}>
          <TopActionMenus AddTitle="Inventory Register" />

          {/* STATISTICS CARDS */}
          <div style={styles.statisticsGrid}>
            <div style={styles.statCard}>
              <div
                style={{
                  ...styles.statIconContainer,
                  backgroundColor: "#E8F5E9",
                }}
              >
                <FontAwesomeIcon
                  icon={faBoxes}
                  style={{ color: "#4CAF50", fontSize: "24px" }}
                />
              </div>
              <div>
                <h3 style={styles.statValue}>{statistics.totalItems}</h3>
                <p style={styles.statLabel}>Total Items</p>
                <small>{statistics.totalQuantity} total units</small>
              </div>
            </div>

            <div style={styles.statCard}>
              <div
                style={{
                  ...styles.statIconContainer,
                  backgroundColor: "#FEF3C7",
                }}
              >
                <FontAwesomeIcon
                  icon={faLowVision}
                  style={{ color: "#F59E0B", fontSize: "24px" }}
                />
              </div>
              <div>
                <h3 style={styles.statValue}>{statistics.lowStockItems}</h3>
                <p style={styles.statLabel}>Low Stock Items</p>
                <small>Below reorder level</small>
              </div>
            </div>

            <div style={styles.statCard}>
              <div
                style={{
                  ...styles.statIconContainer,
                  backgroundColor: "#FEE2E2",
                }}
              >
                <FontAwesomeIcon
                  icon={faClock}
                  style={{ color: "#EF4444", fontSize: "24px" }}
                />
              </div>
              <div>
                <h3 style={styles.statValue}>{statistics.expiredItems}</h3>
                <p style={styles.statLabel}>Expired Items</p>
                <small>Need disposal</small>
              </div>
            </div>

            <div style={styles.statCard}>
              <div
                style={{
                  ...styles.statIconContainer,
                  backgroundColor: "#FEF2F2",
                }}
              >
                <FontAwesomeIcon
                  icon={faBoxOpen}
                  style={{ color: "#DC2626", fontSize: "24px" }}
                />
              </div>
              <div>
                <h3 style={styles.statValue}>{statistics.outOfStockItems}</h3>
                <p style={styles.statLabel}>Out of Stock</p>
                <small>Need restocking</small>
              </div>
            </div>
          </div>

          <div style={styles.statisticsGrid}>
            <div style={styles.statCard}>
              <div
                style={{
                  ...styles.statIconContainer,
                  backgroundColor: "#D1FAE5",
                }}
              >
                <FontAwesomeIcon
                  icon={faCheckCircle}
                  style={{ color: "#10B981", fontSize: "24px" }}
                />
              </div>
              <div>
                <h3 style={styles.statValue}>{statistics.normalStockItems}</h3>
                <p style={styles.statLabel}>Healthy Stock</p>
                <small>Adequate levels</small>
              </div>
            </div>

            <div style={styles.statCard}>
              <div
                style={{
                  ...styles.statIconContainer,
                  backgroundColor: "#E3F2FD",
                }}
              >
                <FontAwesomeIcon
                  icon={faDollarSign}
                  style={{ color: "#2196F3", fontSize: "24px" }}
                />
              </div>
              <div>
                <h3 style={styles.statValue}>
                  GH₵{statistics.totalValue.toFixed(2)}
                </h3>
                <p style={styles.statLabel}>Inventory Value</p>
                <small>Total stock worth</small>
              </div>
            </div>

            <div style={styles.statCard}>
              <div
                style={{
                  ...styles.statIconContainer,
                  backgroundColor: "#FFF3E0",
                }}
              >
                <FontAwesomeIcon
                  icon={faExclamationTriangle}
                  style={{ color: "#E65100", fontSize: "24px" }}
                />
              </div>
              <div>
                <h3 style={styles.statValue}>{statistics.expiringSoon}</h3>
                <p style={styles.statLabel}>Expiring Soon</p>
                <small>Within 30 days</small>
              </div>
            </div>

            <div style={styles.statCard}>
              <div
                style={{
                  ...styles.statIconContainer,
                  backgroundColor: "#F3E5F5",
                }}
              >
                <FontAwesomeIcon
                  icon={faWarehouse}
                  style={{ color: "#9C27B0", fontSize: "24px" }}
                />
              </div>
              <div>
                <h3 style={styles.statValue}>{statistics.uniqueLocations}</h3>
                <p style={styles.statLabel}>Storage Locations</p>
                <small>Active areas</small>
              </div>
            </div>
          </div>

          {/* FILTERS BAR */}
          <div style={styles.filtersBar}>
            <div style={styles.searchWrapper}>
              <FontAwesomeIcon icon={faSearch} style={{ color: "#9CA3AF" }} />
              <input
                type="text"
                style={styles.searchInput}
                placeholder="Search by item name, supplier, or batch number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div style={styles.filterWrapper}>
              <FontAwesomeIcon icon={faFilter} style={{ color: "#9CA3AF" }} />
              <select
                style={styles.filterSelect}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="critical">Critical Stock</option>
                <option value="low">Low Stock</option>
                <option value="normal">In Stock</option>
                <option value="excess">Excess Stock</option>
                <option value="expired">Expired</option>
                <option value="out_of_stock">Out of Stock</option>
              </select>
            </div>

            <div style={styles.filterWrapper}>
              <FontAwesomeIcon
                icon={faMapMarkerAlt}
                style={{ color: "#9CA3AF" }}
              />
              <select
                style={styles.filterSelect}
                value={locationFilter}
                onChange={(e) => setLocationFilter(e.target.value)}
              >
                <option value="all">All Locations</option>
                {storageLocations.map((loc) => (
                  <option key={loc} value={loc}>
                    {loc}
                  </option>
                ))}
              </select>
            </div>

            <button
              style={styles.addButton}
              onClick={() => setShowAddModal(true)}
            >
              <FontAwesomeIcon icon={faPlus} /> Add Item
            </button>
            <button
              style={styles.addButton}
              onClick={() => stateNavigator.navigate("receivedstockregister")}
            >
              <FontAwesomeIcon icon={faTruck} /> Received Register
            </button>
            <button
              style={styles.addButton}
              onClick={() => stateNavigator.navigate("transferstockregister")}
            >
              <FontAwesomeIcon icon={faExchangeAlt} /> Transfer Register
            </button>

            <button
              style={styles.refreshButton}
              onClick={() => mutateInventory()}
            >
              <FontAwesomeIcon icon={faRefresh} />
            </button>
          </div>

          {/* INVENTORY TABLE */}
          {filteredItems.length === 0 ? (
            <div style={styles.emptyState}>
              <FontAwesomeIcon
                icon={faBoxes}
                style={{ fontSize: "64px", color: "#CBD5E1" }}
              />
              <h3 style={styles.emptyTitle}>No Inventory Items Found</h3>
              <p style={styles.emptySubtitle}>
                {searchTerm ||
                statusFilter !== "all" ||
                locationFilter !== "all"
                  ? "No items match your current filters"
                  : "Click 'Add Item' to start tracking your inventory"}
              </p>
            </div>
          ) : (
            <>
              <div style={styles.tableContainer}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th
                        style={styles.th}
                        onClick={() => handleSort("item_name")}
                      >
                        Item Name <SortIcon field="item_name" />
                      </th>
                      <th
                        style={styles.th}
                        onClick={() => handleSort("quantity")}
                      >
                        Quantity <SortIcon field="quantity" />
                      </th>
                      <th style={styles.th}>Storage Location</th>
                      <th
                        style={styles.th}
                        onClick={() => handleSort("days_until_expiry")}
                      >
                        Expiry Date <SortIcon field="days_until_expiry" />
                      </th>
                      <th style={styles.th}>Stock Level</th>
                      <th style={styles.th}>Status</th>
                      <th
                        style={styles.th}
                        onClick={() => handleSort("unit_price")}
                      >
                        Unit Price <SortIcon field="unit_price" />
                      </th>
                      <th style={styles.th}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedItems.map((item) => (
                      <InventoryTableRow key={item.id} item={item} />
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div style={styles.pagination}>
                  <button
                    style={styles.paginationButton}
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                  <span style={styles.paginationInfo}>
                    Page {currentPage} of {totalPages} ({filteredItems.length}{" "}
                    items)
                  </span>
                  <button
                    style={styles.paginationButton}
                    onClick={() =>
                      setCurrentPage((p) => Math.min(totalPages, p + 1))
                    }
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <DashboardContentFooter />

      {/* Add Inventory Modal */}
      {showAddModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <div className="modal-body">
              <div className="modal-header">
                <p>Add New Inventory Item</p>
                <button
                  className="close-modal"
                  onClick={() => setShowAddModal(false)}
                >
                  <FontAwesomeIcon icon={faClose} />
                </button>
              </div>

              <form onSubmit={handleAddItem} className="edit-patient-form">
                <div className="form-group">
                  <label>Item Name *</label>
                  <input
                    type="text"
                    placeholder="Enter item name"
                    value={formData.item_name}
                    onChange={(e) =>
                      setFormData({ ...formData, item_name: e.target.value })
                    }
                    required
                    autoFocus
                  />
                </div>

                <div style={styles.formRow}>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>Quantity *</label>
                    <input
                      type="number"
                      placeholder="0"
                      value={formData.quantity}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          quantity: parseInt(e.target.value) || 0,
                        })
                      }
                      required
                      min="0"
                    />
                  </div>

                  <div className="form-group" style={{ flex: 1 }}>
                    <label>Unit Price (GH₵)</label>
                    <input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={formData.unit_price}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          unit_price: parseFloat(e.target.value) || 0,
                        })
                      }
                    />
                  </div>
                </div>

                <div style={styles.formRow}>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>Storage Location</label>
                    <select
                      style={styles.selectField}
                      value={formData.storage_location}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          storage_location: e.target.value,
                        })
                      }
                    >
                      <option value="">Select Storage Location</option>
                      {STORAGE_LOCATIONS.map((loc) => (
                        <option key={loc} value={loc}>
                          {loc}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group" style={{ flex: 1 }}>
                    <label>Reorder Level</label>
                    <input
                      type="number"
                      placeholder="10"
                      value={formData.reorder_level}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          reorder_level: parseInt(e.target.value) || 10,
                        })
                      }
                    />
                  </div>
                </div>

                <div style={styles.formRow}>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>Expiry Date</label>
                    <input
                      type="date"
                      value={formData.expiry_date}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          expiry_date: e.target.value,
                        })
                      }
                    />
                  </div>

                  <div className="form-group" style={{ flex: 1 }}>
                    <label>Batch Number</label>
                    <input
                      type="text"
                      placeholder="Enter batch number"
                      value={formData.batch_number}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          batch_number: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Supplier</label>
                  <div style={styles.selectWithButton}>
                    <select
                      style={styles.selectField}
                      value={formData.supplier_id}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          supplier_id: e.target.value,
                        })
                      }
                    >
                      <option value="">Select Supplier</option>
                      {suppliers.map((supplier) => (
                        <option key={supplier.nid} value={supplier.nid}>
                          {supplier.name}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      style={styles.inlineAddButton}
                      onClick={() => setShowAddSupplierModal(true)}
                      title="Add New Supplier"
                    >
                      <FontAwesomeIcon icon={faPlus} />
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label>Notes</label>
                  <textarea
                    placeholder="Additional notes"
                    rows="2"
                    value={formData.notes}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        notes: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="submitGroup">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    style={styles.submitButton}
                  >
                    {isSubmitting ? (
                      <FontAwesomeIcon icon={faSpinner} spin />
                    ) : (
                      "Add to Inventory"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Add Supplier Modal */}
      {showAddSupplierModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <div className="modal-body">
              <div className="modal-header">
                <p>Add New Supplier</p>
                <button
                  className="close-modal"
                  onClick={() => setShowAddSupplierModal(false)}
                >
                  <FontAwesomeIcon icon={faClose} />
                </button>
              </div>

              <form onSubmit={handleAddSupplier}>
                <div className="form-group">
                  <label>Supplier Name *</label>
                  <input
                    type="text"
                    placeholder="Enter supplier name"
                    value={newSupplier.name}
                    onChange={(e) =>
                      setNewSupplier({ ...newSupplier, name: e.target.value })
                    }
                    required
                    autoFocus
                  />
                </div>

                <div className="form-group">
                  <label>Contact Person</label>
                  <input
                    type="text"
                    placeholder="Contact person name"
                    value={newSupplier.contact_person}
                    onChange={(e) =>
                      setNewSupplier({
                        ...newSupplier,
                        contact_person: e.target.value,
                      })
                    }
                  />
                </div>

                <div style={styles.formRow}>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>Phone</label>
                    <input
                      type="text"
                      placeholder="Phone number"
                      value={newSupplier.phone}
                      onChange={(e) =>
                        setNewSupplier({
                          ...newSupplier,
                          phone: e.target.value,
                        })
                      }
                    />
                  </div>

                  <div className="form-group" style={{ flex: 1 }}>
                    <label>Email</label>
                    <input
                      type="email"
                      placeholder="Email address"
                      value={newSupplier.email}
                      onChange={(e) =>
                        setNewSupplier({
                          ...newSupplier,
                          email: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Address</label>
                  <textarea
                    placeholder="Supplier address"
                    rows="2"
                    value={newSupplier.address}
                    onChange={(e) =>
                      setNewSupplier({
                        ...newSupplier,
                        address: e.target.value,
                      })
                    }
                  />
                </div>

                <div style={styles.formActions}>
                  <button
                    type="button"
                    style={styles.cancelButton}
                    onClick={() => setShowAddSupplierModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    style={styles.submitButton}
                    disabled={addingSupplier}
                  >
                    {addingSupplier ? (
                      <FontAwesomeIcon icon={faSpinner} spin />
                    ) : (
                      "Add Supplier"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Receive Stock Modal */}
      {showReceiveStockModal && selectedItem && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <div className="modal-body">
              <div className="modal-header">
                <p>Receive Stock - {selectedItem.item_name}</p>
                <button
                  className="close-modal"
                  onClick={() => setShowReceiveStockModal(false)}
                >
                  <FontAwesomeIcon icon={faClose} />
                </button>
              </div>

              <form onSubmit={handleReceiveStock}>
                <div className="form-group">
                  <label>Current Stock</label>
                  <input
                    type="text"
                    value={`${selectedItem.quantity} units`}
                    disabled
                    style={styles.disabledInput}
                  />
                </div>

                <div className="form-group">
                  <label>Current Unit Price</label>
                  <input
                    type="text"
                    value={`GH₵ ${selectedItem.unit_price.toFixed(2)}`}
                    disabled
                    style={styles.disabledInput}
                  />
                </div>

                <div className="form-group">
                  <label>Quantity to Receive *</label>
                  <input
                    type="number"
                    placeholder="Enter quantity"
                    value={receiveForm.quantity}
                    onChange={(e) =>
                      setReceiveForm({
                        ...receiveForm,
                        quantity: parseInt(e.target.value) || 0,
                      })
                    }
                    required
                    min="1"
                    autoFocus
                  />
                </div>

                <div className="form-group">
                  <label>New Unit Cost (GH₵) *</label>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="Enter unit cost for this batch"
                    value={receiveForm.unit_cost}
                    onChange={(e) =>
                      setReceiveForm({
                        ...receiveForm,
                        unit_cost: e.target.value,
                      })
                    }
                    required
                    min="0"
                  />
                  <small style={{ color: "#6B7280" }}>
                    This will update the average unit price using weighted
                    average calculation
                  </small>
                </div>

                <div className="form-group">
                  <label>Purchase Order # (Optional)</label>
                  <input
                    type="text"
                    placeholder="PO-12345"
                    value={receiveForm.purchase_order}
                    onChange={(e) =>
                      setReceiveForm({
                        ...receiveForm,
                        purchase_order: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="form-group">
                  <label>Notes (Optional)</label>
                  <textarea
                    placeholder="Additional notes about this receipt"
                    rows="2"
                    value={receiveForm.notes}
                    onChange={(e) =>
                      setReceiveForm({ ...receiveForm, notes: e.target.value })
                    }
                  />
                </div>

                <div style={styles.formActions}>
                  <button
                    type="button"
                    style={styles.cancelButton}
                    onClick={() => setShowReceiveStockModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    style={styles.submitButton}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <FontAwesomeIcon icon={faSpinner} spin />
                    ) : (
                      "Receive Stock"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Transfer Stock Modal */}
      {showTransferStockModal && selectedItem && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <div className="modal-body">
              <div className="modal-header">
                <p>Transfer Stock - {selectedItem.item_name}</p>
                <button
                  className="close-modal"
                  onClick={() => setShowTransferStockModal(false)}
                >
                  <FontAwesomeIcon icon={faClose} />
                </button>
              </div>

              <form onSubmit={handleTransferStock}>
                <div className="form-group">
                  <label>Available Stock</label>
                  <input
                    type="text"
                    value={`${selectedItem.quantity} units`}
                    disabled
                    style={styles.disabledInput}
                  />
                </div>

                <div className="form-group">
                  <label>Quantity to Transfer *</label>
                  <input
                    type="number"
                    placeholder="Enter quantity"
                    value={transferForm.quantity}
                    onChange={(e) =>
                      setTransferForm({
                        ...transferForm,
                        quantity: parseInt(e.target.value) || 0,
                      })
                    }
                    required
                    min="1"
                    max={selectedItem.quantity}
                  />
                </div>

                <div className="form-group">
                  <label>Destination Department *</label>
                  <select
                    value={transferForm.destination}
                    onChange={(e) =>
                      setTransferForm({
                        ...transferForm,
                        destination: e.target.value,
                      })
                    }
                    required
                  >
                    <option value="">Select Destination</option>
                    {departments.map((dept) => (
                      <option key={dept.id || dept.name} value={dept.name}>
                        {dept.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Reason (Optional)</label>
                  <textarea
                    placeholder="Reason for transfer"
                    rows="2"
                    value={transferForm.reason}
                    onChange={(e) =>
                      setTransferForm({
                        ...transferForm,
                        reason: e.target.value,
                      })
                    }
                  />
                </div>

                <div style={styles.formActions}>
                  <button
                    type="button"
                    style={styles.cancelButton}
                    onClick={() => setShowTransferStockModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    style={styles.submitButton}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <FontAwesomeIcon icon={faSpinner} spin />
                    ) : (
                      "Transfer Stock"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Inventory Modal */}
      {showEditModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <div className="modal-body">
              <div className="modal-header">
                <p>Edit Inventory Item</p>
                <button
                  className="close-modal"
                  onClick={() => setShowEditModal(false)}
                >
                  <FontAwesomeIcon icon={faClose} />
                </button>
              </div>

              <form onSubmit={handleEditItem} className="edit-patient-form">
                <div className="form-group">
                  <label>Item Name *</label>
                  <input
                    type="text"
                    placeholder="Enter item name"
                    value={editFormData.item_name}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        item_name: e.target.value,
                      })
                    }
                    required
                  />
                </div>

                <div style={styles.formRow}>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>Quantity *</label>
                    <input
                      type="number"
                      placeholder="0"
                      value={editFormData.quantity}
                      onChange={(e) =>
                        setEditFormData({
                          ...editFormData,
                          quantity: parseInt(e.target.value) || 0,
                        })
                      }
                      required
                      min="0"
                    />
                  </div>

                  <div className="form-group" style={{ flex: 1 }}>
                    <label>Unit Price (GH₵)</label>
                    <input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={editFormData.unit_price}
                      onChange={(e) =>
                        setEditFormData({
                          ...editFormData,
                          unit_price: parseFloat(e.target.value) || 0,
                        })
                      }
                    />
                  </div>
                </div>

                <div style={styles.formRow}>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>Storage Location</label>
                    <select
                      style={styles.selectField}
                      value={editFormData.storage_location}
                      onChange={(e) =>
                        setEditFormData({
                          ...editFormData,
                          storage_location: e.target.value,
                        })
                      }
                    >
                      <option value="">Select Storage Location</option>
                      {STORAGE_LOCATIONS.map((loc) => (
                        <option key={loc} value={loc}>
                          {loc}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group" style={{ flex: 1 }}>
                    <label>Reorder Level</label>
                    <input
                      type="number"
                      placeholder="10"
                      value={editFormData.reorder_level}
                      onChange={(e) =>
                        setEditFormData({
                          ...editFormData,
                          reorder_level: parseInt(e.target.value) || 10,
                        })
                      }
                    />
                  </div>
                </div>

                <div style={styles.formRow}>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>Expiry Date</label>
                    <input
                      type="date"
                      value={editFormData.expiry_date}
                      onChange={(e) =>
                        setEditFormData({
                          ...editFormData,
                          expiry_date: e.target.value,
                        })
                      }
                    />
                  </div>

                  <div className="form-group" style={{ flex: 1 }}>
                    <label>Batch Number</label>
                    <input
                      type="text"
                      placeholder="Enter batch number"
                      value={editFormData.batch_number}
                      onChange={(e) =>
                        setEditFormData({
                          ...editFormData,
                          batch_number: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Supplier</label>
                  <div style={styles.selectWithButton}>
                    <select
                      style={styles.selectField}
                      value={editFormData.supplier_id}
                      onChange={(e) =>
                        setEditFormData({
                          ...editFormData,
                          supplier_id: e.target.value,
                        })
                      }
                    >
                      <option value="">Select Supplier</option>
                      {suppliers.map((supplier) => (
                        <option key={supplier.nid} value={supplier.nid}>
                          {supplier.name}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      style={styles.inlineAddButton}
                      onClick={() => setShowAddSupplierModal(true)}
                      title="Add New Supplier"
                    >
                      <FontAwesomeIcon icon={faPlus} />
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label>Notes</label>
                  <textarea
                    placeholder="Additional notes"
                    rows="2"
                    value={editFormData.notes}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        notes: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="submitGroup">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    style={styles.submitButton}
                  >
                    {isSubmitting ? (
                      <FontAwesomeIcon icon={faSpinner} spin />
                    ) : (
                      "Update Inventory"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Batches Modal */}
      {showBatchesModal && selectedItem && batchesData && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <div className="modal-header">
              <p>Stock Batches - {selectedItem.item_name}</p>
              <button
                className="close-modal"
                onClick={() => setShowBatchesModal(false)}
              >
                <FontAwesomeIcon icon={faClose} />
              </button>
            </div>
            <div style={{ padding: "16px" }}>
              <div
                style={{
                  marginBottom: "16px",
                  padding: "12px",
                  backgroundColor: "#F3F4F6",
                  borderRadius: "8px",
                }}
              >
                <strong>Total Stock:</strong>{" "}
                {batchesData.item?.current_quantity} units
                <br />
                <strong>Total Value:</strong> GH₵{batchesData.item?.total_value}
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr
                    style={{
                      backgroundColor: "#F9FAFB",
                      borderBottom: "1px solid #E5E7EB",
                    }}
                  >
                    <th style={{ padding: "10px", textAlign: "left" }}>
                      Date Received
                    </th>
                    <th style={{ padding: "10px", textAlign: "left" }}>
                      Quantity
                    </th>
                    <th style={{ padding: "10px", textAlign: "left" }}>
                      Unit Cost
                    </th>
                    <th style={{ padding: "10px", textAlign: "left" }}>
                      Remaining
                    </th>
                    <th style={{ padding: "10px", textAlign: "left" }}>
                      Total Value
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {batchesData.batches?.map((batch) => (
                    <tr
                      key={batch.id}
                      style={{ borderBottom: "1px solid #F0F0F0" }}
                    >
                      <td style={{ padding: "10px" }}>
                        {new Date(batch.received_date).toLocaleDateString()}
                      </td>
                      <td style={{ padding: "10px" }}>
                        {batch.quantity} units
                      </td>
                      <td style={{ padding: "10px" }}>GH₵{batch.unit_cost}</td>
                      <td style={{ padding: "10px" }}>
                        {batch.remaining_quantity} units
                      </td>
                      <td style={{ padding: "10px" }}>GH₵{batch.total_cost}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && selectedItem && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <div className="modal-body">
              <div className="modal-header">
                <p>Confirm Delete</p>
                <button
                  className="close-modal"
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  <FontAwesomeIcon icon={faClose} />
                </button>
              </div>

              <div style={styles.deleteConfirmContent}>
                <FontAwesomeIcon
                  icon={faExclamationTriangle}
                  style={{ fontSize: "48px", color: "#EF4444" }}
                />
                <p>
                  Are you sure you want to delete{" "}
                  <strong>{selectedItem.item_name}</strong> from inventory?
                </p>
                <p style={styles.deleteConfirmSubtext}>
                  This action cannot be undone.
                </p>
              </div>

              <div style={styles.formActions}>
                <button
                  style={styles.cancelButton}
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  Cancel
                </button>
                <button
                  style={styles.deleteConfirmButton}
                  onClick={handleDeleteItem}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <FontAwesomeIcon icon={faSpinner} spin />
                  ) : (
                    "Delete Item"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {modalSuccessMsg && (
        <MessageModal
          leftIcon={faCheckCircle}
          color="green"
          message={successMessage}
          onClose={() => setModalSuccessMsg(false)}
          rightIcon={faClose}
        />
      )}
      {modalError && (
        <MessageModal
          leftIcon={faTimes}
          color="red"
          message={errorMessage}
          onClose={() => setModalError(false)}
          rightIcon={faClose}
        />
      )}
    </div>
  );
}

const styles = {
  container: {
    width: "100%",
    height: "100%",
    backgroundColor: "#f5f7fa",
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
    padding: "24px 32px 4rem 32px",
  },
  loadingContainer: {
    textAlign: "center",
    padding: "80px 20px",
    backgroundColor: "white",
    borderRadius: "16px",
  },
  errorContainer: {
    textAlign: "center",
    padding: "80px 20px",
    backgroundColor: "white",
    borderRadius: "16px",
    color: "#d32f2f",
  },
  retryButton: {
    marginTop: "20px",
    padding: "10px 20px",
    backgroundColor: "#FF6B35",
    color: "white",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
  },
  statisticsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: "20px",
    marginBottom: "24px",
  },
  statCard: {
    background: "white",
    padding: "20px",
    borderRadius: "16px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    display: "flex",
    alignItems: "center",
    gap: "16px",
  },
  statIconContainer: {
    width: "56px",
    height: "56px",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  statValue: {
    margin: 0,
    fontSize: "24px",
    fontWeight: "700",
    color: "#1F2937",
  },
  statLabel: {
    margin: "4px 0 0 0",
    color: "#6B7280",
    fontSize: "15px",
    fontWeight: "500",
  },
  filtersBar: {
    display: "flex",
    gap: "16px",
    marginBottom: "24px",
    flexWrap: "wrap",
    alignItems: "center",
  },
  searchWrapper: {
    flex: 2,
    minWidth: "250px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "12px 16px",
    backgroundColor: "white",
    borderRadius: "12px",
    border: "1px solid #E5E7EB",
  },
  searchInput: {
    flex: 1,
    border: "none",
    fontSize: "15px",
    outline: "none",
    backgroundColor: "transparent",
  },
  filterWrapper: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "12px 16px",
    backgroundColor: "white",
    borderRadius: "12px",
    border: "1px solid #E5E7EB",
  },
  filterSelect: {
    border: "none",
    fontSize: "15px",
    outline: "none",
    backgroundColor: "transparent",
    cursor: "pointer",
    padding: "4px 8px",
  },
  selectWithButton: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
  },
  selectField: {
    flex: 1,
    padding: "8px 12px",
    borderRadius: "6px",
    border: "1px solid #ddd",
    fontSize: "14px",
    backgroundColor: "white",
  },
  inlineAddButton: {
    background: "#28a745",
    color: "white",
    padding: "8px 12px",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "15px",
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  addButton: {
    padding: "10px 20px",
    backgroundColor: "#FF6B35",
    color: "white",
    border: "none",
    borderRadius: "12px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  refreshButton: {
    padding: "10px 16px",
    backgroundColor: "white",
    border: "1px solid #E5E7EB",
    borderRadius: "12px",
    cursor: "pointer",
    color: "#6B7280",
  },
  tableContainer: {
    overflowX: "auto",
    borderRadius: "12px",
    border: "1px solid #E5E7EB",
    backgroundColor: "white",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: "900px",
  },
  th: {
    textAlign: "left",
    padding: "14px 16px",
    backgroundColor: "#000000",
    fontWeight: "600",
    fontSize: "15px",
    color: "#ffffff",
    borderBottom: "1px solid #E5E7EB",
    cursor: "pointer",
    userSelect: "none",
  },
  td: {
    padding: "14px 16px",
    borderBottom: "1px solid #F0F0F0",
    fontSize: "15px",
    color: "#000000",
  },
  expiringRow: {
    backgroundColor: "#FEF2F2",
  },
  batchNumber: {
    fontSize: "10px",
    color: "#9CA3AF",
  },
  quantityBadge: {
    display: "inline-block",
    padding: "4px 10px",
    borderRadius: "20px",
    fontSize: "12px",
    fontWeight: "600",
  },
  expiryWarning: {
    padding: "2px 6px",
    backgroundColor: "#FEE2E2",
    color: "#EF4444",
    borderRadius: "4px",
    fontSize: "10px",
    fontWeight: "600",
    marginLeft: "4px",
  },
  progressBarContainer: {
    width: "100%",
    height: "6px",
    backgroundColor: "#E5E7EB",
    borderRadius: "3px",
    overflow: "hidden",
  },
  progressBar: {
    height: "100%",
    transition: "width 0.3s ease",
  },
  reorderText: {
    fontSize: "15px",
    color: "#9CA3AF",
  },
  statusBadge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "4px 10px",
    borderRadius: "20px",
    fontSize: "15px",
    fontWeight: "600",
    whiteSpace: "nowrap",
  },
  actionButtons: {
    display: "flex",
    gap: "8px",
  },
  receiveButton: {
    background: "#E3F2FD",
    color: "#1976D2",
    border: "none",
    borderRadius: "6px",
    padding: "6px 10px",
    cursor: "pointer",
  },
  transferButton: {
    background: "#FFF3E0",
    color: "#E65100",
    border: "none",
    borderRadius: "6px",
    padding: "6px 10px",
    cursor: "pointer",
  },
  editButton: {
    background: "#E8F5E9",
    color: "#388E3C",
    border: "none",
    borderRadius: "6px",
    padding: "6px 10px",
    cursor: "pointer",
  },
  deleteButton: {
    background: "#FEE2E2",
    color: "#D32F2F",
    border: "none",
    borderRadius: "6px",
    padding: "6px 10px",
    cursor: "pointer",
  },
  pagination: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: "20px",
    paddingTop: "20px",
    borderTop: "1px solid #E5E7EB",
  },
  paginationButton: {
    padding: "8px 16px",
    backgroundColor: "#F9FAFB",
    border: "1px solid #E5E7EB",
    borderRadius: "8px",
    cursor: "pointer",
  },
  paginationInfo: {
    fontSize: "15px",
    color: "#6B7280",
  },
  emptyState: {
    textAlign: "center",
    padding: "80px 20px",
    backgroundColor: "white",
    borderRadius: "16px",
  },
  emptyTitle: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#1F2937",
    marginTop: "16px",
  },
  emptySubtitle: {
    fontSize: "15px",
    color: "#9CA3AF",
    marginTop: "8px",
  },
  formRow: {
    display: "flex",
    gap: "16px",
  },
  submitButton: {
    background: "#FF6B35",
    color: "white",
    padding: "10px 24px",
    border: "none",
    borderRadius: "8px",
    fontSize: "15px",
    cursor: "pointer",
  },
  disabledInput: {
    backgroundColor: "#F9FAFB",
    border: "1px solid #E5E7EB",
    borderRadius: "6px",
    padding: "8px 12px",
    color: "#6B7280",
    cursor: "not-allowed",
  },
  formActions: {
    display: "flex",
    gap: "12px",
    marginTop: "20px",
  },
  cancelButton: {
    flex: 1,
    padding: "10px",
    backgroundColor: "#F3F4F6",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
  },
  deleteConfirmContent: {
    textAlign: "center",
    padding: "20px",
  },
  deleteConfirmSubtext: {
    fontSize: "15px",
    color: "#9CA3AF",
    marginTop: "8px",
  },
  deleteConfirmButton: {
    flex: 1,
    padding: "10px",
    backgroundColor: "#EF4444",
    color: "white",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
  },
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    zIndex: 9999,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  modalContent: {
    backgroundColor: "white",
    borderRadius: "16px",
    maxWidth: "90%",
    maxHeight: "90vh",
    overflow: "auto",
    zIndex: 10000,
    position: "relative",
    padding: "0",
  },
};
